import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { FeaturePermission } from "@/__generated__/featurePermissions";
import { useUser } from "@/auth/useUser";
import { useTwilioTokenLazyQuery } from "@/graphql/queries/twilioToken.graphql.types";
import useJoinConversation from "@/hooks/messages/useJoinConversation";
import { useNotifications } from "@/hooks/messages/useNotifications";
import { useTwilioClient } from "@/hooks/messages/useTwilioClient";
import { MessagesContextType } from "@/types/messages";

export const MessagesContext = createContext<MessagesContextType>(null);

export const useMessagesContextState = () => {
  const { medspa, user, hasFeaturePermission } = useUser();
  const canViewMessages =
    hasFeaturePermission &&
    hasFeaturePermission(FeaturePermission.VIEW_MESSAGES);

  const [getTwilioToken] = useTwilioTokenLazyQuery({
    fetchPolicy: "network-only",
  });

  const getToken = useCallback(async () => {
    if (!user || !medspa || !canViewMessages) return "";
    const { data } = await getTwilioToken({ variables: { id: medspa } });

    return data?.medspaByPk?.additionalInfo?.twilioConversationsToken;
  }, [user, medspa, canViewMessages, getTwilioToken]);

  const { client, conversations, lastMessageSid, clientState, shutdown } =
    useTwilioClient({ getToken });

  const {
    notificationsCount,
    highlevelUnreadMessagesCount,
    calculateNotificationsCount,
    conversationsData,
  } = useNotifications(conversations, Number(medspa));

  const { joinConversation, leaveConversation } = useJoinConversation();

  useEffect(() => {
    if (!user && client) {
      shutdown();
    }
  }, [user, client, shutdown]);

  return useMemo(
    () => ({
      conversations,
      conversationsData,
      clientState,
      lastMessageSid,
      calculateNotificationsCount,
      notificationsCount,
      joinConversation,
      leaveConversation,
      highlevelUnreadMessagesCount,
    }),
    [
      conversations,
      conversationsData,
      clientState,
      lastMessageSid,
      calculateNotificationsCount,
      notificationsCount,
      joinConversation,
      leaveConversation,
      highlevelUnreadMessagesCount,
    ]
  );
};

export const MessagesContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const contextValue = useMessagesContextState();

  return (
    <MessagesContext.Provider value={contextValue}>
      {children}
    </MessagesContext.Provider>
  );
};

export const useMessagesContext = (): MessagesContextType => {
  const context = useContext(MessagesContext);
  if (!context) {
    throw new Error("Must be used inside a MessageContextProvider");
  }

  return context;
};
